import React from 'react';
import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom';

const FooterSection = () => {
  const navigate = useNavigate();

  const footerSections = [
    {
      title: 'Product',
      links: [
        { name: 'For Patients', href: '/patient-dashboard' },
        { name: 'For Providers', href: '/provider-dashboard' },
        { name: 'Cognitive Games', href: '/cognitive-games-section' },
        { name: 'Pricing', href: '#pricing' }
      ]
    },
    {
      title: 'Company',
      links: [
        { name: 'About Us', href: '#about' },
        { name: 'Careers', href: '#careers' },
        { name: 'News', href: '#news' },
        { name: 'Contact', href: '#contact' }
      ]
    },
    {
      title: 'Resources',
      links: [
        { name: 'Help Center', href: '#help' },
        { name: 'API Docs', href: '#api' },
        { name: 'Research', href: '#research' },
        { name: 'Blog', href: '#blog' }
      ]
    },
    {
      title: 'Legal',
      links: [
        { name: 'Privacy Policy', href: '#privacy' },
        { name: 'Terms of Service', href: '#terms' },
        { name: 'HIPAA Compliance', href: '#hipaa' },
        { name: 'Security', href: '#security' }
      ]
    }
  ];

  const socialLinks = [
    { name: 'Twitter', icon: 'Twitter', href: '#twitter' },
    { name: 'LinkedIn', icon: 'Linkedin', href: '#linkedin' },
    { name: 'GitHub', icon: 'Github', href: '#github' },
    { name: 'YouTube', icon: 'Youtube', href: '#youtube' }
  ];

  const handleNavigation = (href) => {
    if (href?.startsWith('/')) {
      navigate(href);
    } else {
      // Handle hash links or external links
      console.log(`Navigate to: ${href}`);
    }
  };

  return (
    <footer className="bg-card border-t border-border">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Main Footer Content */}
          <div className="py-16">
            <div className="grid lg:grid-cols-5 gap-12">
              {/* Brand Section */}
              <div className="lg:col-span-2">
                <div className="flex items-center space-x-2 mb-6">
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                    <Icon name="Brain" size={20} className="text-white" />
                  </div>
                  <span className="text-xl font-semibold text-foreground">MycoMind</span>
                </div>
                
                <p className="text-muted-foreground mb-6 leading-relaxed max-w-md">
                  Bridging the gap between patients and providers with AI-powered insights 
                  for neurological health management.
                </p>

                {/* Contact Info */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <Icon name="Mail" size={16} className="text-muted-foreground" />
                    <a 
                      href="mailto:hello@mycomind.com" 
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      hello@mycomind.com
                    </a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Phone" size={16} className="text-muted-foreground" />
                    <a 
                      href="tel:+1-555-0123" 
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      +1 (555) 012-3456
                    </a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="MapPin" size={16} className="text-muted-foreground" />
                    <span className="text-muted-foreground">
                      San Francisco, CA
                    </span>
                  </div>
                </div>

                {/* Social Media */}
                <div className="flex items-center space-x-4">
                  {socialLinks?.map((social) => (
                    <button
                      key={social?.name}
                      onClick={() => handleNavigation(social?.href)}
                      className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300 group"
                      aria-label={social?.name}
                    >
                      <Icon name={social?.icon} size={16} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Footer Links */}
              <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-8">
                {footerSections?.map((section) => (
                  <div key={section?.title}>
                    <h3 className="font-semibold text-foreground mb-4">
                      {section?.title}
                    </h3>
                    <ul className="space-y-3">
                      {section?.links?.map((link) => (
                        <li key={link?.name}>
                          <button
                            onClick={() => handleNavigation(link?.href)}
                            className="text-muted-foreground hover:text-foreground transition-colors text-left"
                          >
                            {link?.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Newsletter Signup */}
          <div className="py-8 border-t border-border">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="mb-6 md:mb-0">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Stay Updated
                </h3>
                <p className="text-muted-foreground">
                  Get the latest updates on MycoMind features and healthcare insights.
                </p>
              </div>
              
              <div className="flex w-full md:w-auto max-w-md">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-2 border border-border rounded-l-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <button className="px-6 py-2 bg-primary text-primary-foreground rounded-r-lg hover:bg-primary/90 transition-colors font-medium">
                  Subscribe
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="py-6 border-t border-border">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="flex items-center space-x-6 mb-4 md:mb-0">
                <p className="text-sm text-muted-foreground">
                  © {new Date()?.getFullYear()} MycoMind. All rights reserved.
                </p>
              </div>

              {/* Compliance Badges */}
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Icon name="Shield" size={16} className="text-success" />
                  <span className="text-sm text-muted-foreground">HIPAA Compliant</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Lock" size={16} className="text-success" />
                  <span className="text-sm text-muted-foreground">SOC 2 Type II</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Award" size={16} className="text-success" />
                  <span className="text-sm text-muted-foreground">FDA Cleared</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;